import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import {
	TMtdReportsItemResponsePayload,
	TGetMtdReportsRequest,
	TAddCommentRequest,
	IMtdEmReportsItem,
	TGetMtdReportRequest,
	TSendMtdFileReportRequest,
	TCalculateMtdDataRequest,
	TDownloadMtdReportPdfRequest,
	TUpdateObligationsRequest,
	TUpdateObligationsResponse,
	THmrcManualProcessing,
} from './types';
import {
	getMtdReportsRequest,
	getMtdReportsSuccess,
	addMtdCommentRequest,
	addMtdCommentSuccess,
	getMtdReportRequest,
	getMtdReportSuccess,
	sendMtdFileReportRequest,
	sendMtdFileReportSuccess,
	calculateMtdDataRequest,
	calculateMtdDataSuccess,
	downloadMtdReportPdfRequest,
	downloadMtdReportPdfSuccess,
	updateObligationsRequest,
	updateObligationsSuccess,
	getEmLastMtdReportsRequest,
	getEmLastMtdReportsSuccess,
	getEmLastMtdReportsFailure,
	getManualProcessingRequest,
	getManualProcessingSuccess,
	getManualProcessingFailure,
	updateManualProcessingRequest,
	updateManualProcessingSuccess,
	updateManualProcessingFailure,
} from './reducer';

export function* getMtdReportsRequestWorker(action: PayloadAction<TGetMtdReportsRequest>) {
	const { payload } = action;
	try {
		const response: TMtdReportsItemResponsePayload<IMtdEmReportsItem> = yield call(
			api.mtd.getMtdReports,
			payload,
		);
		yield put(getMtdReportsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getMtdReportRequestWorker(action: PayloadAction<TGetMtdReportRequest>) {
	const { payload } = action;
	try {
		const response: IMtdEmReportsItem = yield call(api.mtd.getMtdReport, payload.id);
		yield put(getMtdReportSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* addMtdCommentRequestWorker(action: PayloadAction<TAddCommentRequest>) {
	const { payload } = action;
	try {
		yield call(api.mtd.addComment, payload);
		yield put(addMtdCommentSuccess());
		yield put(getMtdReportsRequest(payload.getMtdReportsRequest ?? {}));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* sendMtdFileReportRequestWorker(action: PayloadAction<TSendMtdFileReportRequest>) {
	const { payload } = action;
	try {
		yield call(api.mtd.sendMtdFileReport, payload);
		yield put(sendMtdFileReportSuccess());
		notificationContainer('File report sent successfully', 'success');
		yield put(getMtdReportsRequest(payload.getMtdReportsRequest ?? {}));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* calculateMtdDataRequestWorker(action: PayloadAction<TCalculateMtdDataRequest>) {
	const { payload } = action;
	try {
		yield call(api.mtd.calculateMtdData, payload);
		yield put(calculateMtdDataSuccess());
		notificationContainer('Report data calculation completed', 'success');
		yield put(getMtdReportRequest({ id: payload.id }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* downloadMtdReportPdfRequestWorker(
	action: PayloadAction<TDownloadMtdReportPdfRequest>,
) {
	const { payload } = action;
	try {
		const blob: Blob = yield call(api.mtd.downloadMtdReportPdf, payload.id);
		yield put(downloadMtdReportPdfSuccess());
		const fallbackName = `hmrc-report-${String(payload.id)}.pdf`;
		fileDownload(blob, payload.fileName ?? fallbackName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* updateObligationsRequestWorker(action: PayloadAction<TUpdateObligationsRequest>) {
	const { payload } = action;
	try {
		const response: TUpdateObligationsResponse = yield call(api.mtd.updateObligations, payload);
		yield put(updateObligationsSuccess(response));
		notificationContainer(
			`Past reports requested successfully. \n Added ${response.reportsCountAdded} reports`,
			'success',
		);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getEmLastMtdReportsRequestWorker(action: PayloadAction<TGetMtdReportsRequest>) {
	const { payload } = action;
	try {
		const response: TMtdReportsItemResponsePayload<IMtdEmReportsItem> = yield call(
			api.mtd.getMtdReports,
			payload,
		);
		yield put(getEmLastMtdReportsSuccess(response));
	} catch (error) {
		yield put(getEmLastMtdReportsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getManualProcessingRequestWorker(action: PayloadAction<number>) {
	try {
		const response: THmrcManualProcessing = yield call(api.mtd.getManualProcessing, action.payload);
		yield put(getManualProcessingSuccess(response));
	} catch (error) {
		yield put(getManualProcessingFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* updateManualProcessingRequestWorker(action: PayloadAction<THmrcManualProcessing>) {
	const { payload } = action;
	try {
		yield call(api.mtd.updateManualProcessing, payload);
		yield put(updateManualProcessingSuccess(payload));
		notificationContainer(
			`HMRC manual processing ${payload.isManual ? 'enabled' : 'disabled'}`,
			'success',
		);
	} catch (error) {
		yield put(updateManualProcessingFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* mtdSaga() {
	yield takeEvery(getMtdReportsRequest.type, getMtdReportsRequestWorker);
	yield takeEvery(addMtdCommentRequest.type, addMtdCommentRequestWorker);
	yield takeEvery(getMtdReportRequest.type, getMtdReportRequestWorker);
	yield takeEvery(sendMtdFileReportRequest.type, sendMtdFileReportRequestWorker);
	yield takeEvery(calculateMtdDataRequest.type, calculateMtdDataRequestWorker);
	yield takeEvery(downloadMtdReportPdfRequest.type, downloadMtdReportPdfRequestWorker);
	yield takeEvery(updateObligationsRequest.type, updateObligationsRequestWorker);
	yield takeEvery(getEmLastMtdReportsRequest.type, getEmLastMtdReportsRequestWorker);
	yield takeEvery(getManualProcessingRequest.type, getManualProcessingRequestWorker);
	yield takeEvery(updateManualProcessingRequest.type, updateManualProcessingRequestWorker);
}
